import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enqueueInfoSnackbar } from '../../action/snackbarCommands';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import ProductCategoriesSection from './ProductCategoriesSection';
import type { InheritedValues, StandardValues } from './categoryConfigRules';
import {
    TenantCheckInCheckOutConfig,
    TenantDetectionCapability,
    TenantInvoiceIssuer,
    TenantPaymentChannel,
    TenantPaymentExpiryReaction,
    TenantPaymentIntegration,
    TenantPayments,
    TenantPaymentsArrangement,
    TenantPaymentsPostpay,
    TenantServiceConfig,
    UpdatePaymentsConfigRequest,
} from '../../api/account/accountApiTypes';
import { useUpdatePaymentsConfigMutation } from '../../api/account/accountApiSlice';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import { cn } from '../../utils/utils';
import { errorMessageFrom } from '../../utils/errorMessage';
import {
    DepositedPrerequisites,
    PAYMENT_TEMPLATE,
    effectivePaymentEntries,
    isSavedServiceEntry,
    unconfiguredDirectBooking,
    unmetPrerequisiteForChannel,
} from './serviceConfigurationRules';
import { resolveBookingWindow } from './bookingWindowRules';

const DETECTIONS: TenantDetectionCapability[] = ['unmonitored', 'monitored', 'adapter'];

/**
 * Defaults offered to a tenant configuring for the first time — the guidance
 * values from ADR-0016 Amendment 2 §1. They are what the form *proposes*;
 * nothing is stored until the tenant saves, so an unconfigured tenant stays
 * unconfigured rather than silently acquiring a policy.
 */
/**
 * Postpay terms start EMPTY, unlike the prepay guidance above. Proposing "5
 * days" would store a validity window the tenant never chose, and an offer
 * that expires is a contract term — the one thing a form must not invent.
 * Empty means the documented rule: valid until the slot starts, payable within
 * 14 days.
 */
const POSTPAY_EMPTY: TenantPaymentsPostpay = {
    offerValidityDays: null,
    paymentTermDays: null,
};

const PREPAY_GUIDANCE = {
    deadlineDays: 14,
    latestWorkingDaysBeforeStart: 3,
    reminderDaysBefore: 3,
    onExpiry: 'notify' as TenantPaymentExpiryReaction,
};

/** The proposed channel is the test-account default — bank transfer — and only
 *  when its prerequisite is met (PO, 2026-08-19). Never "the first channel that
 *  happens to be choosable": that seeded the payment link on any
 *  Stripe-connected tenant, which is how Neuland's form claimed a channel the
 *  tenant does not use. Without bank details nothing is pre-checked and the
 *  Save gate blocks until the tenant picks a channel it can operate. */
const defaultChannels = (deposited: DepositedPrerequisites): TenantPaymentChannel[] =>
    unmetPrerequisiteForChannel(PAYMENT_TEMPLATE.channel, deposited) ? [] : [PAYMENT_TEMPLATE.channel];

/** Reserve first, pay before the start — never postpay, which hands out the
 *  slot on a promise. The template's values (`PAYMENT_TEMPLATE`), which the
 *  backend stores on switch-on (`new-backend/admin/src/lib/serviceTemplate.ts`). */
const defaultPayment = (deposited: DepositedPrerequisites): TenantPaymentsArrangement => ({
    flow: PAYMENT_TEMPLATE.flow,
    channels: defaultChannels(deposited),
});

/**
 * Project stored configuration onto the services the tenant offers. Keyed
 * strictly on those services, so a use case dropped from the tenant's offering
 * does not linger in the config looking configured.
 */
const seedUseCases = (
    services: string[],
    stored: Record<string, TenantServiceConfig> | undefined,
    deposited: DepositedPrerequisites,
): Record<string, TenantServiceConfig> => {
    const next: Record<string, TenantServiceConfig> = {};
    services.forEach((service) => {
        const entry = stored?.[service];
        const charging = entry?.charging ?? PAYMENT_TEMPLATE.charging;
        next[service] = {
            directBooking: entry?.directBooking ?? unconfiguredDirectBooking(service),
            charging,
            ...(charging === 'free'
                ? {}
                : { payment: entry?.payment ?? defaultPayment(deposited) }),
            // Carried through UNCHANGED, including its absence: absent is the
            // documented "standard list" and must survive a save that only
            // touched the payment questions.
            ...(entry?.eventTypes ? { eventTypes: entry.eventTypes } : {}),
            // Server-owned; carried only so the editor can offer these back.
            // The endpoint recomputes it from what is stored and ignores what
            // we send, so round-tripping it here cannot corrupt the history.
            ...(entry?.eventTypesRetired
                ? { eventTypesRetired: entry.eventTypesRetired } : {}),
            // Always seeded, never carried through as absent: the three numbers
            // are always shown in the form, so the form always has a value for
            // them, and `resolveBookingWindow` fills an untouched use case with
            // exactly the default it already behaves as. Saving without
            // touching this block therefore stores the numbers that were
            // already in force rather than changing anything.
            bookingWindow: resolveBookingWindow(entry?.bookingWindow, service),
            // M10: `cancellation` is deliberately NOT seeded. The standard
            // form sends it only once the operator changes it, and the server
            // keeps the stored rule otherwise (review of #3394); the stored
            // value is read for display by `storedCancellation` below.
        };
    });
    return next;
};

/** The tenant-level part — what the Payment settings tab edits, as one unit. */
type PaymentSettingsDraft = Pick<TenantPayments, 'detection' | 'prepay'> & {
    postpay: TenantPaymentsPostpay;
};

const settingsCanonical = (p: PaymentSettingsDraft): string =>
    [
        p.detection,
        p.prepay.deadlineDays,
        p.prepay.latestWorkingDaysBeforeStart,
        p.prepay.reminderDaysBefore,
        p.prepay.onExpiry,
        p.postpay?.offerValidityDays ?? '',
        p.postpay?.paymentTermDays ?? '',
    ].join('~');

type ServiceConfigurationSectionProps = {
    readOnly: boolean;
    /** Stored configuration of the tenant IN VIEW — for a master this is the
     *  by-slug tenant, not the caller's own. Passed in rather than re-queried:
     *  a `useGetAccountQuery()` here would always resolve to the caller's own
     *  tenant and show a master the wrong tenant's configuration (#1334). */
    payments: TenantPayments | null;
    /** Use cases the tenant offers — the same source the booking surfaces use. */
    services: string[] | null;
    /** Derived Stripe status. Choices depend on it; it is never chosen here. */
    integration: TenantPaymentIntegration | undefined;
    /** Whether bank details are deposited — the prerequisite for a transfer,
     *  and for an invoice kolula issues. */
    hasBankDetails: boolean;
    /** WHICH issuer is stored (ADR-0012 WP0): any issuer unlocks the invoice
     *  channel, `kolula` also needs the bank details, and the channel names
     *  who actually sends the bill. */
    invoiceIssuer: TenantInvoiceIssuer | null | undefined;
    /** The stored check-in/check-out flow. Read here for what each standard
     *  product category shows; its `enabled` switch is written through the
     *  category's own save (ADR-0026 §AF), its content only by the
     *  check-in tab. */
    checkInCheckOutFlow: TenantCheckInCheckOutConfig | null | undefined;
    /** The tenant's booking page, for each space category's public plan (#3075). */
    bookingPage: { slug: string, active: boolean };
    /** Identity of the tenant in view; seeding re-runs when (and only when)
     *  this changes. Keying on the response object instead would re-seed on
     *  every unrelated `['Account']` invalidation and wipe unsaved edits. */
    tenantKey: string | undefined;
    /** Tab visibility (PO, 2026-08-19: Services is tabbed). Hidden with CSS
     *  rather than unmounted, so unsaved edits survive a tab switch. */
    showUseCases: boolean;
    showPaymentSettings: boolean;
};

/**
 * Services › Use Cases and Services › Payment settings.
 *
 * **Use Cases** is one card per use case, its heading over its product
 * categories (ADR-0026 §F). Every category — the STANDARD one included, a real
 * category since §AE — is configured with the same form, `CategoryConfigForm`,
 * through its own endpoint (§AG). There is nothing left to configure on the use
 * case itself.
 *
 * **Payment settings** is the tenant-level half of `payments`: detection,
 * prepay deadlines, postpay terms. Its save sends no use cases — the endpoint
 * then keeps the stored ones (§AF), so this tab can never write a stale copy
 * over a category saved in the meantime.
 *
 * It does not configure the Stripe connection, the bank details or the invoice
 * issuer. Those are deposited in Account (ADR-0010); the category form reports
 * them as prerequisites and links there.
 */
const ServiceConfigurationSection = ({
    readOnly,
    payments,
    services: tenantServices,
    integration,
    hasBankDetails,
    invoiceIssuer,
    checkInCheckOutFlow,
    bookingPage,
    tenantKey,
    showUseCases,
    showPaymentSettings,
}: ServiceConfigurationSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updatePayments, { isLoading }] = useUpdatePaymentsConfigMutation();
    // Live categories, for the tenant-wide payment terms below: a category may
    // settle differently from its use case (ADR-0026 §U).
    const { data: categories } = useTenantProductCategoriesQuery();

    const services = useMemo(() => tenantServices ?? [], [tenantServices]);
    const deposited = useMemo<DepositedPrerequisites>(
        () => ({
            // Only the tenant's OWN credentials count: `shared_credentials`
            // resolves to another tenant's account, which is the #997 failure
            // mode, so it is not a connection for this purpose.
            stripeConnected: integration?.stripe === 'connected',
            bankDetails: hasBankDetails,
            invoiceIssuer: invoiceIssuer ?? null,
        }),
        [integration, hasBankDetails, invoiceIssuer],
    );

    // Payment settings are one editable unit (design-decisions › Bearbeiten
    // und Speichern): the view shows what is stored, "Bearbeiten" opens a
    // draft of it. Nothing is seeded into separate form state, so a refetch
    // some other section triggers cannot overwrite an edit in progress, and
    // the view always shows the stored values.
    const savedSettings = useMemo<PaymentSettingsDraft>(
        () => ({
            detection: payments?.detection ?? 'unmonitored',
            prepay: payments?.prepay ?? PREPAY_GUIDANCE,
            postpay: payments?.postpay ?? POSTPAY_EMPTY,
        }),
        [payments],
    );
    const settingsDraft = useEditableDraft<PaymentSettingsDraft>(
        savedSettings,
        (a, b) => settingsCanonical(a) === settingsCanonical(b),
    );
    const { detection, prepay, postpay } = settingsDraft.value;
    const setPrepay = (next: TenantPayments['prepay']) => settingsDraft.setDraft({ ...settingsDraft.value, prepay: next });
    const setPostpay = (next: TenantPaymentsPostpay) => settingsDraft.setDraft({ ...settingsDraft.value, postpay: next });
    const [error, setError] = useState<string | null>(null);

    // A tenant switch drops an open draft rather than carrying it over.
    const { cancel: cancelSettings } = settingsDraft;
    useEffect(() => {
        cancelSettings();
        setError(null);
    }, [tenantKey, cancelSettings]);

    /** Each use case's standard category as stored — or, never saved, the
     *  proposed standard (the template). Always the STORED values, never an
     *  unsaved edit: a category inherits what bookings run on. */
    const storedUseCases = useMemo(
        () => seedUseCases(services, payments?.useCases, deposited),
        [services, payments, deposited],
    );

    const current = useMemo<TenantPayments>(
        () => ({ detection, prepay, postpay, useCases: storedUseCases }),
        [detection, prepay, postpay, storedUseCases],
    );

    // A tenant that has never saved sees the guidance defaults, which equal the
    // saved baseline — so gating Save on a change alone would make them
    // unsaveable. Saving them unchanged is what stores them.
    const settingsSaveable = settingsDraft.isDirty || (settingsDraft.editing && !payments?.prepay);

    const handleSave = async () => {
        setError(null);
        // No `useCases`: the endpoint keeps the stored ones (ADR-0026 §AF).
        const payload: UpdatePaymentsConfigRequest = { prepay, postpay, detection };
        try {
            await updatePayments(payload).unwrap();
            settingsDraft.finish();
        } catch (e) {
            setError(errorMessageFrom(e, t('services.configuration.save_failed'), t));
        }
    };

    const fieldClass =
        'w-full rounded-[5px] border border-foreground/15 bg-text-contrast px-3 py-2'
        + ' text-sm text-foreground';
    const hintClass = 'text-xs text-foreground/60';

    /** M10: the use case's stored cancellation rule — shown, never re-sent. */
    const storedCancellation = (service: string) => {
        const cancellation = payments?.useCases?.[service]?.cancellation;
        return cancellation ? { cancellation } : {};
    };

    /**
     * What every further category of a use case inherits (ADR-0026 §S): the
     * STORED values of its standard product category. For a tenant that never
     * saved, the proposed standard, and `saved: false` makes the caption say so.
     */
    const inheritedFor = (service: string): InheritedValues => {
        const entry = storedUseCases[service]
            ?? seedUseCases([service], undefined, deposited)[service];
        return {
            // A template entry stored on switch-on is still the proposal.
            saved: isSavedServiceEntry(payments?.useCases?.[service]),
            checkIn: checkInCheckOutFlow?.useCases?.[service]?.enabled === true,
            charging: entry.charging,
            directBooking: entry.directBooking,
            ...(entry.charging !== 'free' && entry.payment?.channels.length
                ? { payment: { flow: entry.payment.flow, channels: entry.payment.channels } }
                : {}),
            ...(entry.eventTypes ? { eventTypes: entry.eventTypes } : {}),
            bookingWindow: resolveBookingWindow(entry.bookingWindow, service),
            ...storedCancellation(service),
        };
    };

    /** The standard product category's booking values (ADR-0026 §AF): the use
     *  case's entry, its check-in switch, and our template for the reset. */
    const standardFor = (service: string): StandardValues => ({
        entry: {
            ...(storedUseCases[service] ?? seedUseCases([service], undefined, deposited)[service]),
            ...storedCancellation(service),
        },
        template: seedUseCases([service], undefined, deposited)[service],
        // A template entry stored on switch-on is still the proposal.
        saved: isSavedServiceEntry(payments?.useCases?.[service]),
        checkInEnabled: checkInCheckOutFlow?.useCases?.[service]?.enabled === true,
        // At least one module on — the check-in tab stores an all-off entry
        // for every use case, so the entry alone proves nothing (§AF).
        checkInAuthored: Object.values(checkInCheckOutFlow?.useCases?.[service]?.modules ?? {})
            .some((on) => on === true),
    });

    // Every arrangement a booking can be settled under: each use case's
    // standard category, plus each live further category's override of it
    // (ADR-0026 §U). A post-pay category under a pre-pay use case needs the
    // post-pay terms below as much as a post-pay use case does.
    const effective = useMemo(
        () => effectivePaymentEntries(
            current,
            (categories ?? []).filter((c) => services.includes(c.useCase) && !c.isStandard),
        ),
        [current, categories, services],
    );

    // The deadline terms apply to reservations that are paid before the start,
    // so they are pointless on a tenant whose every offer is free. Tenant-level
    // rather than per offer: nobody has asked for per-offer deadlines, and one
    // payment discipline per house is the normal case (ADR-0016 Amendment 3).
    const anyChargeable = effective.some((e) => e.charging !== 'free');

    // Which payment shapes are actually configured: the prepay deadline block
    // (and its on-expiry reaction) only applies while some chargeable offer
    // still collects before the start, the postpay terms only while some offer
    // runs postpay (innodom review 3.3). State behind a hidden block is kept
    // and saved unchanged, so flipping a flow back restores the values.
    const anyPrepayFlow = effective.some(
        (e) => e.charging !== 'free' && e.payment?.flow === 'reserve_pay_first',
    );
    const anyPostpayFlow = effective.some(
        (e) => e.charging !== 'free' && e.payment?.flow === 'reserve_accept_postpay',
    );

    /** The payment settings — locked in the view, editable in the draft. */
    const renderSettings = (locked: boolean) => {
        if (!anyChargeable) {
            return (
                <p className={'text-sm text-foreground/60'}>
                    {t('services.configuration.payment_settings_all_free')}
                </p>
            );
        }
        return (
            <div className={'flex flex-col gap-6'}>
                <div>
                    <label
                        htmlFor={'service-detection'}
                        className={'block text-sm font-medium text-foreground mb-1'}
                    >
                        {t('services.configuration.detection')}
                    </label>
                    <select
                        id={'service-detection'}
                        className={fieldClass}
                        value={detection}
                        disabled={locked}
                        onChange={(e) => {
                            const next = e.target.value as TenantDetectionCapability;
                            // `cancel` needs a way to notice payment — same
                            // coercion reason as the charging change above,
                            // and the same announcement when it flips. One
                            // draft write: two would each start from the same
                            // draft and the second would undo the first.
                            const resetExpiry = next === 'unmonitored' && prepay.onExpiry === 'cancel';
                            settingsDraft.setDraft({
                                ...settingsDraft.value,
                                detection: next,
                                ...(resetExpiry ? { prepay: { ...prepay, onExpiry: 'notify' } } : {}),
                            });
                            if (resetExpiry) {
                                dispatch(enqueueInfoSnackbar({
                                    message: t('services.configuration.on_expiry_reset_snackbar'),
                                }));
                            }
                        }}
                    >
                        {DETECTIONS.map((d) => (
                            <option key={d} value={d}>
                                {t(`services.configuration.detection_${d}`)}
                            </option>
                        ))}
                    </select>
                    <p className={`${hintClass} mt-1`}>
                        {t('services.configuration.detection_hint')}
                    </p>
                </div>

                {anyPrepayFlow && (<div>
                    <h3 className={'text-sm font-medium text-foreground mb-1'}>
                        {t('services.configuration.prepay')}
                    </h3>
                    <p className={`${hintClass} mb-3`}>
                        {t('services.configuration.prepay_hint')}
                    </p>
                    {/* items-end keeps the inputs on one line when a label
                    wraps to two — otherwise the middle field sits lower. */}
                    <div className={'grid grid-cols-1 sm:grid-cols-3 gap-3 items-end'}>
                        {([
                            ['deadlineDays', 'services.configuration.deadline_days'],
                            ['latestWorkingDaysBeforeStart', 'services.configuration.latest_before_start'],
                            ['reminderDaysBefore', 'services.configuration.reminder_days'],
                        ] as const).map(([field, label]) => (
                            <div key={field} className={'flex flex-col justify-end'}>
                                <label
                                    htmlFor={`prepay-${field}`}
                                    className={'block text-sm font-medium text-foreground mb-1'}
                                >
                                    {t(label)}
                                </label>
                                <input
                                    id={`prepay-${field}`}
                                    type={'number'}
                                    min={0}
                                    className={fieldClass}
                                    value={prepay[field]}
                                    disabled={locked}
                                    onChange={(e) =>
                                        setPrepay({ ...prepay, [field]: Number(e.target.value) })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>)}

                {/* Postpay terms (#2318) — the two dates an OFFER carries,
                beside the prepay ones because they answer the same
                shape of question. Left empty they are not stored: an
                offer that expires is a contract term, so the form must
                not invent one. */}
                {anyPostpayFlow && (<div>
                    <h3 className={'text-sm font-semibold text-foreground mb-1'}>
                        {t('services.configuration.postpay_title')}
                    </h3>
                    <p className={`${hintClass} mb-3`}>
                        {t('services.configuration.postpay_hint')}
                    </p>
                    <div className={'grid grid-cols-1 sm:grid-cols-2 gap-3 items-end'}>
                        {([
                            ['offerValidityDays', 'services.configuration.offer_validity_days', 'services.configuration.offer_validity_placeholder'],
                            ['paymentTermDays', 'services.configuration.payment_term_days', 'services.configuration.payment_term_placeholder'],
                        ] as const).map(([field, label, placeholder]) => (
                            <div key={field} className={'flex flex-col justify-end'}>
                                <label
                                    htmlFor={`postpay-${field}`}
                                    className={'block text-sm font-medium text-foreground mb-1'}
                                >
                                    {t(label)}
                                </label>
                                <input
                                    id={`postpay-${field}`}
                                    type={'number'}
                                    min={1}
                                    className={fieldClass}
                                    // Empty string, not 0: clearing the field must restore
                                    // the documented rule, and a stored 0 would be a
                                    // different rule (an offer expiring on arrival).
                                    value={postpay[field] ?? ''}
                                    placeholder={t(placeholder)}
                                    disabled={locked}
                                    onChange={(e) =>
                                        setPostpay({
                                            ...postpay,
                                            [field]: e.target.value === '' ? null : Number(e.target.value),
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>)}

                {anyPrepayFlow && (<div>
                    <label
                        htmlFor={'service-on-expiry'}
                        className={'block text-sm font-medium text-foreground mb-1'}
                    >
                        {t('services.configuration.on_expiry')}
                    </label>
                    <select
                        id={'service-on-expiry'}
                        className={fieldClass}
                        value={prepay.onExpiry}
                        disabled={locked}
                        onChange={(e) =>
                            setPrepay({
                                ...prepay,
                                onExpiry: e.target.value as TenantPaymentExpiryReaction,
                            })
                        }
                    >
                        <option value={'notify'}>
                            {t('services.configuration.on_expiry_notify')}
                        </option>
                        <option value={'cancel'} disabled={detection === 'unmonitored'}>
                            {t('services.configuration.on_expiry_cancel')}
                        </option>
                    </select>
                    {detection === 'unmonitored' && (
                        <p className={`${hintClass} mt-1`}>
                            {t('services.configuration.on_expiry_blocked')}
                        </p>
                    )}
                </div>)}
            </div>
        );
    };

    return (
        <>
            {/* One card per use case (PO, 2026-08-19: the boundaries between
                use cases must be unmistakable — same layout as the Account
                tab's product blocks), under one intro card. */}
            <div className={cn('flex flex-col gap-6', !showUseCases && 'hidden')}>
                <section
                    id={'service-configuration'}
                    className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}
                >
                    <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                        {t('services.configuration.title')}
                    </h2>
                    <p className={'text-sm text-foreground/60'}>
                        {t('services.configuration.description')}
                    </p>

                    {services.length === 0 && (
                        <p className={'text-sm text-foreground/60 mt-4'}>
                            {t('services.configuration.no_services')}
                        </p>
                    )}
                </section>

                {/* One card per use case: the use case is the heading, its
                    standard product category the first row, further
                    categories below (ADR-0026 §F, §AE). Keyed on the tenant so
                    a switch resets an open form instead of leaking a
                    half-typed category into another tenant's screen. */}
                <ProductCategoriesSection
                    key={`product-categories-${tenantKey}`}
                    readOnly={readOnly}
                    services={services}
                    inheritedFor={inheritedFor}
                    standardFor={standardFor}
                    deposited={deposited}
                    invoiceIssuer={invoiceIssuer}
                    bookingPage={bookingPage}
                />
            </div>

            <div className={cn(!showPaymentSettings && 'hidden')}>
                <EditableSection
                    id={'payment-settings'}
                    title={t('services.configuration.payment_settings_title')}
                    description={t('services.configuration.payment_settings_desc')}
                    // Nothing to set while every offer is free.
                    readOnly={readOnly || !anyChargeable}
                    editing={settingsDraft.editing}
                    isDirty={settingsSaveable}
                    saving={isLoading}
                    error={error}
                    onEdit={settingsDraft.startEdit}
                    onCancel={() => { setError(null); settingsDraft.cancel(); }}
                    onSave={() => { void handleSave(); }}
                    view={renderSettings(true)}
                >
                    {renderSettings(false)}
                </EditableSection>
            </div>
        </>
    );
};

export default ServiceConfigurationSection;
